import { getFullBucketName, S3Bucket } from '../config/constants';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  uploadData as storageUploadData,
  UploadDataWithPathOutput
} from 'aws-amplify/storage';

const sanitizeFileName = (filename: string) => {
  // This regular expression finds special characters and replaces them with an underscore
  return filename.replace(/[^\w.]+/g, '_');
};

const getBasePath = ({
  file,
  customFileName
}: {
  file: File;
  customFileName?: string;
}) => {
  const [fileNameWithoutExt, ext] = file.name.split('.');

  if (customFileName) {
    return `${customFileName}.${ext}`;
  }

  return `${sanitizeFileName(fileNameWithoutExt)}_${uuidv4()}.${ext}`;
};

const getPath = ({
  file,
  customFileName,
  prefix
}: {
  file: File;
  customFileName?: string;
  prefix?: string;
}) => {
  const basePath = getBasePath({ file, customFileName });
  return prefix ? `${prefix}/${basePath}` : basePath;
};

type UseProps = {
  bucket: S3Bucket;
  customFileName?: string;
  prefix?: string;
};

type FunctionProps = {
  file: File;
  metadata?: { [key: string]: string };
};

export const useAmplifyUploadData = ({
  bucket,
  customFileName,
  prefix
}: UseProps) => {
  if (bucket === S3Bucket.PUBLIC && !prefix) {
    throw new Error('Public bucket requires a prefix');
  }

  const [uploadTask, setUploadTask] = useState<UploadDataWithPathOutput | null>(
    null
  );
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadData = async ({ file, metadata }: FunctionProps) => {
    try {
      setIsUploading(true);

      const path = getPath({
        file,
        customFileName,
        prefix
      });

      const task = storageUploadData({
        path,
        data: file,
        options: {
          bucket: getFullBucketName(bucket),
          contentType: file.type,
          metadata,
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              setUploadProgress(
                Math.round((transferredBytes / totalBytes) * 100)
              );
            }
          }
        }
      });
      setUploadTask(task);
      const result = await task.result;
      setUploadResult(result);
      setUploadTask(null);
      return result;
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const cancelUpload = () => {
    if (uploadTask) {
      uploadTask.cancel();
    }
  };

  return {
    uploadData,
    cancelUpload,
    uploadResult,
    uploadProgress,
    isUploading
  };
};
